import { SecureStorageItemTypes } from "./constants";
import type { SecureStorageItems, SecureStorageService } from "./types";

const parseValue = <K extends keyof SecureStorageItems>(
  key: K,
  value: string,
): SecureStorageItems[K] | null => {
  if (SecureStorageItemTypes[key] === "string") {
    return value;
  }
  if (SecureStorageItemTypes[key] === "number") {
    return Number(value) as unknown as SecureStorageItems[K];
  }
  if (SecureStorageItemTypes[key] === "boolean") {
    return (value === "true") as unknown as SecureStorageItems[K];
  }
  if (SecureStorageItemTypes[key] === "object") {
    return JSON.parse(value) as SecureStorageItems[K];
  }
  throw new Error("Unsupported value type");
};

const serializeValue = <K extends keyof SecureStorageItems>(
  key: K,
  value: SecureStorageItems[K],
) => {
  const type = SecureStorageItemTypes[key];

  if (type === "string") {
    if (typeof value !== "string") throw Error("String value expected");
    return value;
  }
  if (type === "number") {
    if (typeof value !== "number") throw Error("Number value expected");
    return (value as number).toString();
  }
  if (type === "boolean") {
    if (typeof value !== "boolean") throw Error("Boolean value expected");
    return (value as boolean).toString();
  }
  if (type === "object") {
    if (typeof value !== "object") throw Error("Object value expected");
    return JSON.stringify(value);
  }

  throw new Error("Unsupported value type");
};

const SecureStorage: SecureStorageService = {
  get: (key) => {
    try {
      const value = localStorage.getItem(key);

      if (value === null) return null;

      return parseValue(key, value);
    } catch {
      return null;
    }
  },

  getAsync: async (key) => SecureStorage.get(key),

  set: (key, value) => {
    const valueToStore = serializeValue(key, value);

    localStorage.setItem(key, valueToStore);
  },

  setAsync: async (key, value) => {
    SecureStorage.set(key, value);
  },

  remove: async (key) => localStorage.removeItem(key),

  clear: async () => localStorage.clear(),
};

export default SecureStorage;
